export type PlanType = 'essentiel' | 'metier' | 'pilotage' | 'daf' | null;
export type ModuleType = 'agri' | 'associations' | 'pme' | null;

export interface PermissionFlags {
  is_metier: boolean;
  is_pilotage: boolean;
  is_daf: boolean;
}

export interface UserPlan {
  current_plan: PlanType;
  selected_metier_module: ModuleType;
  is_metier: boolean;
  is_pilotage: boolean;
  is_daf: boolean;
  is_associations: boolean;
  is_clients: boolean;
  is_funding: boolean;
}

export interface Permissions {
  access_core: boolean;
  access_modules_metiers: boolean;
  access_automated_reports: boolean;
  access_daf_space: boolean;
  can_manage_subventions: boolean;
  can_manage_agri: boolean;
  can_manage_pme: boolean;
  can_add_monthly_daf_notes: boolean;
  max_bank_accounts: number;
}

export const CORE_FEATURES = [
  'Tableau de bord',
  'Trésorerie (vision temps réel)',
  'Transactions manuelles',
  'Catégorisation automatique',
  'Plan de trésorerie',
  'Scénarios de trésorerie',
  'Stress Test',
  'Fonds de Roulement Dynamique',
  'Budget prévisionnel',
  'Suivi budgétaire'
] as const;

export function calculatePermissions(userPlan: UserPlan | null): Permissions {
  if (!userPlan || !userPlan.current_plan) {
    return {
      access_core: false,
      access_modules_metiers: false,
      access_automated_reports: false,
      access_daf_space: false,
      can_manage_subventions: false,
      can_manage_agri: false,
      can_manage_pme: false,
      can_add_monthly_daf_notes: false,
      max_bank_accounts: 2
    };
  }

  const {
    current_plan,
    selected_metier_module,
    is_metier,
    is_pilotage,
    is_daf,
    is_associations = false,
    is_clients = false,
    is_funding = false
  } = userPlan;

  const permissions: Permissions = {
    access_core: true,
    access_modules_metiers: is_metier || is_pilotage || is_daf,
    access_automated_reports: is_pilotage || is_daf,
    access_daf_space: is_daf,
    can_manage_subventions: is_associations || (is_metier && selected_metier_module === 'associations'),
    can_manage_agri: is_funding || (is_metier && selected_metier_module === 'agri'),
    can_manage_pme: is_clients || (is_metier && selected_metier_module === 'pme'),
    can_add_monthly_daf_notes: is_daf,
    max_bank_accounts: 2
  };

  if (current_plan === 'daf') {
    permissions.max_bank_accounts = 999;
  } else if (current_plan === 'pilotage') {
    permissions.max_bank_accounts = 5;
  }

  return permissions;
}

export function getPlanDisplayName(plan: PlanType): string {
  switch (plan) {
    case 'essentiel':
      return 'Pack Essentiel';
    case 'metier':
      return 'Pack Métier';
    case 'pilotage':
      return 'Pack Pilotage';
    case 'daf':
      return 'Pack DAF';
    default:
      return 'Aucun plan';
  }
}

export function getModuleDisplayName(module: ModuleType): string {
  switch (module) {
    case 'agri':
      return 'Module Agriculture';
    case 'associations':
      return 'Module Associations';
    case 'pme':
      return 'Module PME';
    default:
      return 'Aucun module';
  }
}

export function needsModuleSelection(userPlan: UserPlan | null): boolean {
  if (!userPlan) return false;

  const hasModuleAccess = userPlan.is_metier || userPlan.is_pilotage || userPlan.is_daf;
  const hasModuleSelected = !!userPlan.selected_metier_module;

  return hasModuleAccess && !hasModuleSelected;
}

export function canDowngradeTo(currentPlan: PlanType, targetPlan: PlanType): { allowed: boolean; warnings: string[] } {
  const warnings: string[] = [];

  if (currentPlan === 'daf' && targetPlan !== 'daf') {
    warnings.push('Vous perdrez l\'accès à l\'espace DAF et aux comptes rendus personnalisés');
  }

  if ((currentPlan === 'daf' || currentPlan === 'pilotage') && (targetPlan !== 'daf' && targetPlan !== 'pilotage')) {
    warnings.push('Vous perdrez l\'accès aux rapports financiers automatisés');
  }

  if ((currentPlan === 'metier' || currentPlan === 'pilotage' || currentPlan === 'daf') && targetPlan === 'essentiel') {
    warnings.push('Vous perdrez l\'accès à votre module métier sélectionné');
  }

  return {
    allowed: true,
    warnings
  };
}
